import { type FC, type ReactNode, useEffect, useRef } from 'react';
import classnames from 'classnames';

import ChevronDown from 'dibs-icons/exports/ChevronDown.ts';

import styles from './TradeBenefitsAccordionItem.scss';
import dibsCss from 'dibs-css';

type Variant = 'small' | 'medium';

type Props = {
    summary: ReactNode;
    children: ReactNode;
    variant?: Variant;
    contentId?: string;
    openOnHashes?: readonly string[];
    contentClassName?: string;
    onOpen?: () => void;
};

const SUMMARY_FONT_CLASS: Record<Variant, string> = {
    small: dibsCss.sassyFontBodyTypeHeavy,
    medium: dibsCss.sassyFontBodyHeaderMediumNormal,
};

const SUMMARY_PADDING_CLASS: Record<Variant, string> = {
    small: dibsCss.pySmall,
    medium: dibsCss.pyMedium,
};

const CONTENT_PADDING_CLASS: Record<Variant, string> = {
    small: dibsCss.pbSmall,
    medium: dibsCss.pbMedium,
};

const TradeBenefitsAccordionItem: FC<Props> = ({
    summary,
    children,
    variant = 'small',
    contentId,
    openOnHashes,
    contentClassName,
    onOpen,
}) => {
    const detailsRef = useRef<HTMLDetailsElement>(null);

    useEffect(() => {
        if (!contentId) {
            return undefined;
        }

        const openIfTargeted = (): void => {
            const details = detailsRef.current;

            if (!details || details.open) {
                return;
            }

            const hash = window.location.hash.slice(1);
            const isDirectTarget = hash === contentId;
            const isAlternateTarget = openOnHashes?.includes(hash) ?? false;

            if (!isDirectTarget && !isAlternateTarget) {
                return;
            }

            details.open = true;

            if (isDirectTarget) {
                details.scrollIntoView({ block: 'start' });
            }
        };

        openIfTargeted();
        window.addEventListener('hashchange', openIfTargeted);
        return () => window.removeEventListener('hashchange', openIfTargeted);
    }, [contentId, openOnHashes]);

    useEffect(() => {
        const details = detailsRef.current;

        if (!details || !onOpen) {
            return undefined;
        }

        const handleToggle = (): void => {
            if (details.open) {
                onOpen();
            }
        };

        details.addEventListener('toggle', handleToggle);
        return () => details.removeEventListener('toggle', handleToggle);
    }, [onOpen]);

    return (
        <li className={styles.item}>
            <details
                id={contentId}
                ref={detailsRef}
                className={classnames(
                    styles.details,
                    dibsCss.borderT,
                    dibsCss.borderTSolid,
                    dibsCss.borderTStorm
                )}
                data-tn={`trade-benefits-accordion-item-${contentId}`}
            >
                <summary
                    className={classnames(
                        styles.summary,
                        variant === 'small' ? styles.summarySmall : styles.summaryMedium,
                        SUMMARY_FONT_CLASS[variant],
                        SUMMARY_PADDING_CLASS[variant],
                        dibsCss.cursorPointer,
                        dibsCss.flex,
                        dibsCss.itemsCenter,
                        dibsCss.justifyBetween,
                        dibsCss.gapSmall
                    )}
                    data-tn="trade-benefits-accordion-item-summary"
                >
                    <span>{summary}</span>
                    <span className={styles.chevron} aria-hidden>
                        <ChevronDown variation="20" />
                    </span>
                </summary>
                <div
                    className={classnames(
                        dibsCss.sassyFontBody,
                        CONTENT_PADDING_CLASS[variant],
                        contentClassName
                    )}
                    data-tn="trade-benefits-accordion-item-content"
                >
                    {children}
                </div>
            </details>
        </li>
    );
};

export { TradeBenefitsAccordionItem };
